import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import Breadcrumbs from '../components/Breadcrumbs';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';

export default function ForIndividuals() {
  useSEO({
    title: 'AI Mock Interviews & Career Readiness Platform | GreenTick',
    description: 'Master your next interview with GreenTick AI. Access industry-grade mock interviews, instant behavioral feedback, and personalized performance reports to land your dream role.',
    canonicalPath: '/for-individuals',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      <BreadcrumbsJsonLd 
        items={[
          { name: 'Home', item: '/' },
          { name: 'For Individuals', item: '/for-individuals' }
        ]}
      />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs 
              items={[{ name: 'For Individuals', item: '/for-individuals' }]} 
            />

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Best Preparation
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                For You
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Elevate your interview skills with conversational AI training, resume refinement, and personalized performance reports.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Smooth Interview Experience</h3>
                <p className="text-slate-400">
                  Preparing for interviews through Green Tick was a smooth and interactive experience. The instant feedback helped understand where you stand and what needs improvement.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Engaging AI Interviews</h3>
                <p className="text-slate-400">
                  AI-driven interviews are surprisingly engaging. It feels like having a real conversation, and personalized questions make the experience more relevant to your skills.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Stress-Free Process</h3>
                <p className="text-slate-400">
                  Green Tick makes the job application process straightforward and stress-free. The immediate feedback on responses is very helpful.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Build Confidence</h3>
                <p className="text-slate-400">
                  Instant feedback makes you feel more confident about your application and helps you prepare better for future interviews.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">What's Included</h2>
              <div className="space-y-4">
                {[
                  'Conversational Interview Training',
                  'Resume Refinement',
                  'Technical Round Practice',
                  'Actionable Performance Reports',
                  'Career Guidance',
                  'Instant Feedback',
                  'Personalized Learning Path'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://dashboard.greentick.org.in/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Try Demo
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
